import { useEffect, useMemo, useState } from 'react';
import {
  vergelijkVersies,
  type Registratie,
  type RegistratieVersie,
  type Verschil,
} from '@/types/domain';
import { getHistorieService } from '@/services/serviceFactory';
import { datumNL, vandaagISO } from '@/utils/format';
import { Knop } from '@/components/ui/Knop';
import { Laadvlak } from '@/components/ui/Feedback';
import './form.css';

interface HistorieProps {
  /** De huidige stand; het eindpunt van de reeks wijzigingen. */
  registratie: Registratie;
}

/** Eén wijziging: wat er veranderde en of er contact aan vooraf ging. */
interface Wijziging {
  id: string;
  wanneer: string;
  coach: string;
  /** Bij deze opslag kwam er een contactmoment bij. */
  metContact: boolean;
  verschillen: Verschil[];
}

/** Of een wijziging van vandaag is; die krijgt een donkere stip op de tijdlijn. */
function isVandaag(wanneer: string): boolean {
  return wanneer.slice(0, 10) === vandaagISO();
}

/** Aantal wijzigingen dat standaard zichtbaar is. */
const EERST_TONEN = 3;

/**
 * Het wijzigingslogboek van één adres.
 *
 * Dit is nadrukkelijk niet de tijdlijn van bezoeken — die staat in de sectie
 * Contact. Hier staat wie wanneer wát heeft aangepast, inclusief correcties.
 * Omdat correcties zich snel opstapelen worden alleen de laatste paar
 * getoond; de rest staat achter een knop.
 */
export function Historie({ registratie }: HistorieProps): JSX.Element {
  const [versies, setVersies] = useState<RegistratieVersie[] | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [allesTonen, setAllesTonen] = useState(false);

  useEffect(() => {
    let actief = true;
    setVersies(null);
    setFout(null);
    setAllesTonen(false);

    getHistorieService()
      .haalVoor(registratie.id)
      .then((gevonden) => {
        if (actief) setVersies(gevonden);
      })
      .catch((error: unknown) => {
        if (!actief) return;
        setFout(error instanceof Error ? error.message : 'Historie kon niet worden geladen.');
        setVersies([]);
      });

    return () => {
      actief = false;
    };
  }, [registratie.id, registratie.versie]);

  /**
   * Elke bewaarde versie vergeleken met wat erna kwam.
   *
   * De historie bewaart de stand vóór een wijziging. Om te zien wát er
   * veranderde moet je hem dus naast zijn opvolger leggen: de nieuwste
   * bewaarde versie naast de huidige stand, de rest naast de versie erboven.
   */
  const wijzigingen = useMemo<Wijziging[]>(() => {
    if (!versies) return [];

    return versies.map((versie, index) => {
      const opvolger = index === 0 ? registratie : versies[index - 1].momentopname;

      return {
        id: versie.id,
        wanneer: versie.vervangenOp,
        coach: opvolger.coach || versie.coach,
        metContact:
          opvolger.contactmomenten.length > versie.momentopname.contactmomenten.length,
        verschillen: vergelijkVersies(versie.momentopname, opvolger),
      };
    });
  }, [versies, registratie]);

  if (versies === null) {
    return <Laadvlak tekst="Wijzigingen laden..." regels={3} />;
  }

  if (fout) {
    return <p className="veld__fout">{fout}</p>;
  }

  if (wijzigingen.length === 0) {
    return (
      <p className="sectie__toelichting">
        Deze registratie is één keer opgeslagen en daarna niet meer aangepast. Zodra er iets
        verandert, staat hier wie dat wanneer heeft gedaan.
      </p>
    );
  }

  const zichtbaar = allesTonen ? wijzigingen : wijzigingen.slice(0, EERST_TONEN);
  const verborgen = wijzigingen.length - zichtbaar.length;

  return (
    <>
      <p className="sectie__toelichting">
        Wie heeft wanneer iets aangepast. De bezoeken en gesprekken zelf staan bij Contact.
      </p>

      <ol className="wijzigingen">
        {zichtbaar.map((wijziging) => (
          <li
            key={wijziging.id}
            className={`wijziging${isVandaag(wijziging.wanneer) ? ' wijziging--vandaag' : ''}`}
          >
            <div className="wijziging__kop">
              <span className="wijziging__wanneer">{datumNL(wijziging.wanneer)}</span>
              <span className="wijziging__coach">{wijziging.coach || 'onbekend'}</span>
              {wijziging.metContact && (
                <span className="wijziging__merk">nieuw contact</span>
              )}
            </div>

            {wijziging.verschillen.length === 0 ? (
              <p className="wijziging__leeg">
                {wijziging.metContact
                  ? 'Alleen een contactmoment toegevoegd.'
                  : 'Kleine aanpassing.'}
              </p>
            ) : (
              <ul className="wijziging__lijst">
                {wijziging.verschillen.map((verschil) => (
                  <li key={verschil.veld}>
                    <span className="wijziging__veld">{verschil.veld}: </span>
                    <span className="wijziging__van">{verschil.van}</span>{' '}
                    <span aria-label="werd">→</span>{' '}
                    <span className="wijziging__naar">{verschil.naar}</span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>

      {verborgen > 0 && (
        <Knop variant="subtiel" compact breed onClick={() => setAllesTonen(true)}>
          Toon {verborgen} oudere wijziging{verborgen === 1 ? '' : 'en'}
        </Knop>
      )}
    </>
  );
}
